import { getProductTypeFromTypename } from 'bundles/collections-carousel/constants';
import type { CollectionEntity } from 'bundles/collections-carousel/types/CarouselCollection';
import { LEARNING_PRODUCTS } from 'bundles/search-common/constants';
import type { ProductCardSimilarProductFragmentFragment as ProductCardSimilarProductFragment } from 'bundles/unified-description-page-common/queries/__generated__/ProductCardSimilarProductFragment';
import {
  getCollectionEntity,
  mapDifficultyLevel,
} from 'bundles/unified-description-page-common/utils/dataTransformation';

export const convertXdpSimilarProductToCollectionEntity = (
  product: ProductCardSimilarProductFragment,
  entityIndex: number,
  removeFreeTrialBadge: boolean = false
): CollectionEntity | null => {
  const { id, marketingProductType, productTypeAttributes, badges } = product;

  let badgesToUse = badges || [];

  if (removeFreeTrialBadge) {
    badgesToUse = badgesToUse.filter((badge) => badge !== 'Free Trial');
  }

  // Handle Specialization products
  if (
    productTypeAttributes?.__typename === 'ProductCard_Specialization' &&
    productTypeAttributes.canonical?.latestSpecialization
  ) {
    const { isPathwayContent, isPartOfCourseraPlus, hasTopInstructor, totalAssignmentCount } = productTypeAttributes;
    const {
      cardImageUrl,
      difficultyLevel,
      name,
      partners,
      slug,
      rating,
      urlPath,
      skillTags,
      coBrandingEnabled,
      launchedAt,
    } = productTypeAttributes.canonical.latestSpecialization;

    let productType;
    if (marketingProductType === 'SPECIALIZATION') productType = 'SPECIALIZATION' as const;
    else if (marketingProductType === 'PROFESSIONAL_CERTIFICATE') productType = 'PROFESSIONAL CERTIFICATE' as const;

    return getCollectionEntity({
      productId: id,
      name,
      slug,
      imageUrl: cardImageUrl,
      badges: badgesToUse,
      productType,
      urlPath,
      isPartOfCourseraPlus,
      reviewCount: rating?.ratingCount ?? 0,
      rating: rating?.averageFiveStarRating ?? 0,
      isPathwayContent,
      productDifficultyLevel: mapDifficultyLevel(difficultyLevel),
      partners: partners.map((partner) => ({
        id: partner.id,
        name: partner.name,
        squareLogo: partner.squareLogo ?? '',
      })),
      skillsTags:
        skillTags?.map((skill) => ({
          id: skill.skillId,
          name: skill.skillName,
          isToolSoftware: skill.isToolSoftware,
        })) ?? [],
      coBrandingEnabled,
      lastUpdated: launchedAt ?? undefined,
      totalAssignmentCount,
      hasTopInstructor,
      entityIndex,
    });
  }

  // Handle Course products (includes regular courses, guided projects, and projects)
  if (productTypeAttributes?.__typename === 'ProductCard_Course' && productTypeAttributes.canonical) {
    const { isPathwayContent, isPartOfCourseraPlus, rating, reviewCount } = productTypeAttributes;
    const { id: courseId, slug, name, difficultyLevel, promoPhotoUrl, partners } = productTypeAttributes.canonical;

    // Determine product type from marketingProductType
    // This properly differentiates between COURSE, PROJECT, and GUIDED_PROJECT
    const productType = getProductTypeFromTypename(marketingProductType);

    // Determine URL path based on product type
    // Projects (guided/regular) use /projects/{slug}, courses use /learn/{slug}
    const urlPath =
      productType === LEARNING_PRODUCTS.Project || productType === LEARNING_PRODUCTS.GuidedProject
        ? `/projects/${slug}`
        : `/learn/${slug}`;

    return getCollectionEntity({
      productId: courseId,
      name,
      slug,
      imageUrl: promoPhotoUrl ?? '',
      badges: badgesToUse,
      productType,
      urlPath,
      isPartOfCourseraPlus,
      reviewCount,
      rating,
      isPathwayContent,
      productDifficultyLevel: difficultyLevel ? mapDifficultyLevel(difficultyLevel) : undefined,
      partners: partners.map((partner) => ({
        id: partner.id,
        name: partner.name,
        squareLogo: partner.squareLogo ?? '',
      })),
      entityIndex,
    });
  }

  return null;
};
